/**
 * External Imports
 */
import {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo
} from 'react';
import { connect, useDispatch } from 'react-redux';
/**
 * Internal Imports
 */
import RoundsReportTable from '../../../components/customTables/reports/patient/roundsReportTable';
import SearchHeader from '../../../components/searchHeader';

import { RootState } from '../../../redux/store';
import {
  clearRoundsReportFilter,
  getCSVToExportSuccess,
  getDataToDownloadSuccess
} from '../../../redux/actions';
import { date as dt } from '../../../utils/helpers';
import {
  useGetPatientRoundsReportQuery,
  useLazyGetPatientRoundsReportDownloadQuery
} from '../../../redux/apis/Reports.api';
/**
 * Global Type Definition Imports
 */
import {
  AuthUserProps,
  Filter,
  PatientRoundsReport,
  IUnitWithDisplayName
} from '../../../config/interfaces';

/**
 * Style Imports
 */
import { ReportContainer } from '../styles/ReportsViews.styles';

interface RoundsReportProps {
  reportSearchQuery: string;
  searchTerm: string;
  authorizedUser: AuthUserProps;
  selectedUnits: IUnitWithDisplayName[];
  roundsReportFilter: Filter;
  currentPatientRoundsReportPage: number;
  sortKey: string;
  sortDir: string;
  setSearchTerm: Dispatch<SetStateAction<string>>;
}

const RoundsReport: FC<RoundsReportProps> = ({
  reportSearchQuery,
  searchTerm,
  authorizedUser,
  selectedUnits,
  roundsReportFilter,
  currentPatientRoundsReportPage,
  sortKey,
  sortDir,
  setSearchTerm
}) => {
  const dispatch = useDispatch();

  const [triggerPatientRoundsExport] =
    useLazyGetPatientRoundsReportDownloadQuery();
  const {
    data: patientRoundsReportData,
    // isError: isGetPatientRoundsReportError,
    isFetching: isGetPatientRoundsReportFetching,
    isLoading: isGetPatientRoundsReportLoading
  } = useGetPatientRoundsReportQuery(
    {
      facilityId: authorizedUser?.facilityId,
      unitId: selectedUnits.map(item => item.id),
      query: reportSearchQuery || '',
      roundedBy: roundsReportFilter.roundedBy || '',
      sortBy: sortKey,
      sortOrder: sortDir,
      startday: dt.formatFilterStartDate(
        roundsReportFilter.startDate ?? dt.todayDateWithDashes()
      ),
      endday: dt.formatFilterEndDate(
        roundsReportFilter.endDate ?? dt.todayDateWithDashes()
      ),
      page: currentPatientRoundsReportPage
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !authorizedUser?.facilityId || !selectedUnits?.length
    }
  );

  useEffect(() => {
    return () => {
      dispatch(clearRoundsReportFilter());
    };
  }, []);

  const listToDisplay = useMemo(() => {
    if (patientRoundsReportData?.roundReportList?.length) {
      const roundCleanData =
        selectedUnits?.length > 0
          ? patientRoundsReportData?.roundReportList.reduce(
              (
                cleanData: PatientRoundsReport[],
                round: PatientRoundsReport
              ) => {
                // Note: there must be a standardization for detecting the 'unable to round' records.
                // The below logic will cover the existing data.
                return [
                  ...cleanData,
                  {
                    roundId: round?.roundId || '',
                    unit: round?.unit || '',
                    unitId: round?.unitId || '',
                    room: round?.room || '',
                    patientId: round?.patientId || '',
                    patient: `${round?.patientFirstName} ${round?.patientLastName}`,
                    patientFirstName: round?.patientFirstName || '',
                    patientLastName: round?.patientLastName || '',
                    patientMRN: round?.patientMRN || '',
                    createdUserName: `${round?.userFirstName} ${round?.userLastName}`,
                    createdUserId: round?.userId || '',
                    accountNumber: round?.accountNum || '',
                    facilityId: round?.facilityId || '',
                    status:
                      round.status === 'rounded'
                        ? 'Rounded'
                        : 'Unable To Round',
                    date:
                      round?.roundDate && dt.isOrbitTimestamp(round?.roundDate)
                        ? dt.parseDate(round?.roundDate).toLocaleString([], {
                            day: '2-digit',
                            month: '2-digit',
                            year: '2-digit'
                          })
                        : '',
                    time:
                      round?.roundDate && dt.isOrbitTimestamp(round?.roundDate)
                        ? dt.parseDate(round?.roundDate).toLocaleString([], {
                            hour: '2-digit',
                            minute: '2-digit'
                          })
                        : '',
                    roundDate: round?.roundDate || ''
                  }
                ];
              },
              []
            )
          : [];
      return roundCleanData;
    }
    return [];
  }, [patientRoundsReportData, selectedUnits]);

  const exportParams = useMemo(
    () => ({
      facilityId: authorizedUser?.facilityId,
      unitId: selectedUnits.map(item => item.id),
      query: reportSearchQuery || '',
      roundedBy: roundsReportFilter.roundedBy || '',
      sortBy: sortKey,
      sortOrder: sortDir,
      startday: dt.formatFilterStartDate(
        roundsReportFilter.startDate ?? dt.todayDateWithDashes()
      ),
      endday: dt.formatFilterEndDate(
        roundsReportFilter.endDate ?? dt.todayDateWithDashes()
      )
    }),
    [
      authorizedUser?.facilityId,
      reportSearchQuery,
      roundsReportFilter,
      sortKey,
      sortDir,
      selectedUnits
    ]
  );

  const handleExportCsv = useCallback(() => {
    triggerPatientRoundsExport(exportParams, true)
      .unwrap()
      .then(data => dispatch(getCSVToExportSuccess(data)))
      .catch(() => {});
  }, [exportParams]);

  const handleClickDownloadPDF = useCallback(() => {
    triggerPatientRoundsExport(exportParams, true)
      .unwrap()
      .then(data => dispatch(getDataToDownloadSuccess(data)))
      .catch(() => {});
  }, [exportParams]);

  return (
    <ReportContainer id="Patient-Rounds-Report-Container">
      <SearchHeader
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        handleExportCsv={handleExportCsv}
        handleClickDownloadPDF={handleClickDownloadPDF}
      />
      <RoundsReportTable
        roundsReportList={listToDisplay}
        loading={
          isGetPatientRoundsReportLoading || isGetPatientRoundsReportFetching
        }
        totalTableCount={patientRoundsReportData?.totalRecordCount}
      />
    </ReportContainer>
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    patientRoundsReport: state.ReportReducer.patientRoundsReport,
    reportSearchQuery: state.ReportReducer.reportSearchQuery,
    currentPatientRoundsReportPage:
      state.ReportReducer.currentPatientRoundsReportPage,
    sortKey: state.ReportReducer.sortRoundsKey,
    sortDir: state.ReportReducer.sortRoundsDir,
    roundsReportFilter: state.ReportReducer.roundsReportFilter,
    selectedUnits: state.ConfigReducer.selectedUnits,
    authorizedUser: state.AuthorizedUser.authorizedUser
  };
};

export default connect(mapReduxStateToProps)(RoundsReport);
